import { Tooltip } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import { tradersApi } from 'api';
import { StatusToggle } from 'components';
import { QueryKey, TraderWorkStatus, UserRole, UserStatus } from 'enums';
import { useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { Trader } from 'types';

type Props = {
  trader: Trader;
  role: UserRole;
};

export const TraderWorkStatusToggle: React.FC<Props> = ({ trader, role }) => {
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.traders.actions',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.status_toggle',
  });
  const queryClient = useQueryClient();

  const isBlocked = useMemo(
    () => trader.user?.status === UserStatus.Disabled,
    [trader.user?.status],
  );

  const title = useMemo(() => {
    if (isBlocked) {
      return t('work_status_blocked');
    }
    return trader.workStatus === TraderWorkStatus.Active
      ? t('work_status_deactivate')
      : tCommon('activate');
  }, [isBlocked, t, tCommon, trader.workStatus]);

  const mutation = useMutation(tradersApi.updateWorkStatusAsRole(role), {
    onSuccess: () => queryClient.invalidateQueries(QueryKey.Traders),
  });

  const handleUpdateStatus = useCallback(
    (workStatus: TraderWorkStatus) =>
      mutation.mutate({ id: trader.id, workStatus }),
    [mutation, trader.id],
  );

  const toggle = (
    <StatusToggle
      status={trader.workStatus}
      activeStatus={TraderWorkStatus.Active}
      inactiveStatus={TraderWorkStatus.Inactive}
      disabled={
        mutation.isLoading ||
        (isBlocked && trader.workStatus !== TraderWorkStatus.Active)
      }
      hideTitle
      updateStatus={handleUpdateStatus}
    />
  );

  return (
    <Tooltip title={title}>
      <span>{toggle}</span>
    </Tooltip>
  );
};
